import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChatMessage, AgentChatUser } from "@/components/agent/chat/types";
import { Tables } from "@/types/database.types";
import { saveUserMessage as saveUserMessageService } from "@/services/messages";
import { trackConversation } from "@/services/safeUsageTracking";

export const useMessageSending = (
  agent: Tables<"agents">,
  organization: Tables<"organizations">,
  conversationId: string | null,
  setConversationId: (id: string | null) => void,
  agentChatUser: AgentChatUser | null,
  setAgentChatUser: (user: AgentChatUser | null) => void,
  isPublic: boolean,
) => {
  const createConversationIfNeeded = async () => {
    if (conversationId) return conversationId;

    let currentConversationId = null;

    try {
      if (isPublic) {
        console.log("Creating public conversation for agent:", agent.id);

        // Only proceed if we have an agent chat user
        if (!agentChatUser) {
          console.log("No agent chat user found, creating one...");

          try {
            // Get stored ID from localStorage if exists
            const storedUserId = localStorage.getItem(
              `agent_chat_user_id_${agent.public_id}`,
            );

            if (storedUserId) {
              // Fetch the existing agent chat user
              const { data: existingUser, error: fetchError } = await supabase
                .from("agent_chat_users")
                .select("*")
                .eq("id", storedUserId)
                .single();

              if (fetchError || !existingUser) {
                console.error(
                  "Couldn't find stored user, creating new one:",
                  fetchError,
                );
              } else {
                console.log("Found existing agent chat user:", existingUser);
                setAgentChatUser(existingUser);

                // Create conversation with existing agent_chat_user_id
                const { data: newConversation, error } = await supabase
                  .from("conversations")
                  .insert({
                    agent_id: agent.id,
                    user_id: null,
                    agent_chat_user_id: existingUser.id,
                    state: "open",
                  })
                  .select()
                  .single();

                if (error) {
                  console.error("Error creating public conversation:", error);
                  throw new Error(
                    error.message || "Failed to create conversation",
                  );
                }

                console.log(
                  "Created public conversation with existing user:",
                  newConversation,
                );
                currentConversationId = newConversation.id;
                setConversationId(newConversation.id);

                // Track conversation usage (non-blocking)
                trackConversation(agent.id).catch((error) =>
                  console.warn("Failed to track conversation:", error),
                );

                if (agent.public_id) {
                  localStorage.setItem(
                    `public_chat_${agent.public_id}`,
                    newConversation.id,
                  );
                }

                return currentConversationId;
              }
            }

            // This code will run if no stored user ID exists or if fetching the user failed
            const { data: newChatUser, error: chatUserError } = await supabase
              .from("agent_chat_users")
              .insert({
                name: "Anonymous User", // Fallback name
                is_anonymous: true,
              })
              .select()
              .single();

            if (chatUserError) {
              console.error("Error creating anonymous user:", chatUserError);
              throw new Error(chatUserError.message || "Failed to create user");
            }

            setAgentChatUser(newChatUser);
            console.log("Created fallback agent chat user:", newChatUser);

            // Save the new user ID to localStorage
            if (agent.public_id) {
              localStorage.setItem(
                `agent_chat_user_id_${agent.public_id}`,
                newChatUser.id,
              );
            }
          } catch (userErr) {
            console.error("Failed to create fallback user:", userErr);
            throw new Error(
              "Please provide your contact information before starting the chat.",
            );
          }
        }

        // Create conversation with agent_chat_user_id
        const { data: newConversation, error } = await supabase
          .from("conversations")
          .insert({
            agent_id: agent.id,
            user_id: null,
            agent_chat_user_id: agentChatUser?.id,
            state: "open", // Set initial state to open
          })
          .select()
          .single();

        if (error) {
          console.error("Error creating public conversation:", error);
          throw new Error(error.message || "Failed to create conversation");
        }

        console.log("Created public conversation:", newConversation);
        currentConversationId = newConversation.id;
        setConversationId(newConversation.id);

        // Track conversation usage (non-blocking)
        trackConversation(agent.id).catch((error) =>
          console.warn("Failed to track conversation:", error),
        );

        if (agent.public_id) {
          localStorage.setItem(
            `public_chat_${agent.public_id}`,
            newConversation.id,
          );
        }
      } else {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        if (!userId) {
          throw new Error("User not authenticated");
        }

        const { data: newConversation, error } = await supabase
          .from("conversations")
          .insert({
            agent_id: agent.id,
            user_id: userId,
            agent_chat_user_id: null,
            state: "open", // Set initial state to open
          })
          .select()
          .single();

        if (error) throw error;

        currentConversationId = newConversation.id;
        setConversationId(newConversation.id);

        // Track conversation usage (non-blocking)
        trackConversation(agent.id).catch((error) =>
          console.warn("Failed to track conversation:", error),
        );
      }

      return currentConversationId;
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error;
    }
  };

  const saveUserMessage = async (
    conversationId: string,
    messageText: string,
  ): Promise<void> => {
    try {
      const { error } = await saveUserMessageService(
        conversationId,
        messageText,
      );
      if (error) {
        console.error("Error saving user message:", error);
      }
    } catch (error) {
      console.error("Failed to save user message:", error);
    }
  };

  return {
    createConversationIfNeeded,
    saveUserMessage,
  };
};
